/**
 * MemoryStage — base scene for the prologue memory stages (P2–P6). Launched in parallel above the
 * caller (which is paused), paints its own tint + vignette, runs one timeline through a
 * CinematicPlayer, reports the result, stops itself and resumes the caller.
 *
 * Subclasses (prologue builder) override `buildStage()` to spawn actors/props and may override
 * `createHost()`. Register them with `registerMemoryStages([{ key, timeline, sceneKey, sceneClass }])`.
 * Input: Enter/Space/Z/click advance dialogue, Esc/X skip the timeline (when `skippable`).
 */
import Phaser from 'phaser';
import type { IActor, IDialogueBox } from '@/core/types/contracts';
import type { Facing } from '@/core/types';
import { Depth, FONT_BODY, GAME_H, GAME_W } from '@/core/config';
import { ctxOf } from '@/core/context';
import { SceneKeys } from '@/core/scenes/keys';
import { createActor } from '@/characters';
import { DialogueRunner, hooksFromContext, type DialogueHooks } from '@/dialogue/DialogueRunner';
import { resolveDialogueBox } from '@/dialogue/DevDialogueBox';
import { CinematicPlayer, type CinematicHost, type CinematicResult } from './CinematicPlayer';
import { createSceneCinematicHost, type CinematicStateAccess } from './SceneHost';

export interface MemoryStageStyle {
  bg: number;          // backdrop colour
  tint: number;        // overlay tint colour
  tintAlpha: number;   // overlay alpha
  vignette: { radius: number; strength: number; color: number };
  label: string;       // dev label (Chinese, live text)
}

/** Per-memory tints from ART_DIRECTION.md. */
export const MEMORY_STAGE_STYLES: Record<string, MemoryStageStyle> = {
  'memory-storm': { bg: 0x0a0e1c, tint: 0x1a2340, tintAlpha: 0.35, vignette: { radius: 0.55, strength: 0.85, color: 0x000000 }, label: '黑夜风浪' },
  'memory-island': { bg: 0x3a3f47, tint: 0x9aa3ad, tintAlpha: 0.25, vignette: { radius: 0.6, strength: 0.6, color: 0x2a2f38 }, label: '无名小岛' },
  'memory-black-water': { bg: 0x1c1208, tint: 0xd08a2a, tintAlpha: 0.22, vignette: { radius: 0.45, strength: 0.9, color: 0x000000 }, label: '星塘病榻' },
  'memory-farewell': { bg: 0x9fb2c4, tint: 0xe8f0f6, tintAlpha: 0.3, vignette: { radius: 0.65, strength: 0.5, color: 0x5a6b7c }, label: '冬日告别' },
  'memory-sword-training': { bg: 0x5a4622, tint: 0xd9a34a, tintAlpha: 0.28, vignette: { radius: 0.6, strength: 0.6, color: 0x3a2a12 }, label: '路上习剑' },
  default: { bg: 0x101418, tint: 0x2b3a5c, tintAlpha: 0.3, vignette: { radius: 0.6, strength: 0.7, color: 0x000000 }, label: '回忆' },
};

export interface MemoryStageDef {
  key: string;
  timeline: string;
  /** Phaser scene key of the subclass; defaults to the generic base scene. */
  sceneKey?: string;
  sceneClass?: new () => MemoryStage;
  style?: Partial<MemoryStageStyle>;
}
export const MEMORY_STAGES: Record<string, MemoryStageDef> = {};
export function registerMemoryStages(list: MemoryStageDef[]): void { for (const d of list) MEMORY_STAGES[d.key] = d; }

/** Stage scene keys whose launch has been queued but not yet settled (Phaser applies `launch` at the next step). */
const LAUNCHING = new Set<string>();
/** True while a memory stage scene is launching, active or paused (showcases/scenes use it for their busy checks). */
export function isMemoryStageRunning(mgr: Phaser.Scenes.ScenePlugin, sceneKey?: string): boolean {
  const keys = sceneKey ? [sceneKey] : [SceneKeys.MEMORY_STAGE, ...Object.values(MEMORY_STAGES).map((d) => d.sceneKey ?? SceneKeys.MEMORY_STAGE)];
  return keys.some((k) => LAUNCHING.has(k) || (!!mgr.get(k) && (mgr.isActive(k) || mgr.isPaused(k))));
}

export interface MemoryStageData {
  stageKey: string;
  timelineId: string;
  callerKey: string;
  fastForward?: boolean;
  state?: CinematicStateAccess;
  dialogueBox?: IDialogueBox;
  letter?: (paragraph: string, opts: { instant: boolean }) => Promise<void>;
  onDone?: (result: CinematicResult) => void;
}

export const MEMORY_DONE = 'memory:done';

export class MemoryStage extends Phaser.Scene {
  protected stageData: MemoryStageData = { stageKey: '', timelineId: '', callerKey: '' };
  protected style: MemoryStageStyle = MEMORY_STAGE_STYLES['default'] as MemoryStageStyle;
  protected readonly actors = new Map<string, IActor>();
  protected player: CinematicPlayer | null = null;
  protected dialogue: DialogueRunner | null = null;
  private finished = false;

  constructor(key: string = SceneKeys.MEMORY_STAGE) { super(key); }

  init(data: MemoryStageData): void {
    this.stageData = data;
    const def = MEMORY_STAGES[data.stageKey];
    const base = MEMORY_STAGE_STYLES[data.stageKey] ?? MEMORY_STAGE_STYLES['default'] as MemoryStageStyle;
    this.style = { ...base, ...(def?.style ?? {}) };
    this.finished = false;
    this.actors.clear();
  }

  create(): void {
    this.buildBackdrop();
    this.buildStage();
    const box = this.stageData.dialogueBox ?? resolveDialogueBox(this);
    this.dialogue = new DialogueRunner(box, this.createDialogueHooks(), {
      host: {
        face: (id, facing) => this.actors.get(id)?.setFacing(facing),
        emote: (id, emote) => void this.actors.get(id)?.showEmote(emote),
        wait: (ms) => new Promise<void>((r) => { this.time.delayedCall(ms, r); }),
      },
    });
    this.player = new CinematicPlayer(this.createHost());
    this.wireInput();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.teardown, this);
    void this.runTimeline();
  }

  /** Override to spawn actors/props for this memory. */
  protected buildStage(): void { /* base: empty stage */ }

  protected buildBackdrop(): void {
    const st = this.style;
    this.cameras.main.setBackgroundColor(st.bg);
    this.add.rectangle(0, 0, GAME_W, GAME_H, st.tint, st.tintAlpha).setOrigin(0).setDepth(Depth.LIGHTING).setScrollFactor(0);
    try {
      this.cameras.main.filters.external.addVignette(0.5, 0.5, st.vignette.radius, st.vignette.strength, st.vignette.color);
    } catch (e) { console.warn('[memory-stage] vignette filter unavailable', e); }
    this.add.text(8, 6, `回忆 · ${st.label}  [dev stage: ${this.stageData.stageKey}]`, { fontFamily: FONT_BODY, fontSize: '11px', color: '#c9b89a' }).setDepth(Depth.UI).setScrollFactor(0).setAlpha(0.8);
  }

  protected spawnActor(id: string, actorKey: string, x: number, y: number, facing: Facing): IActor {
    const a = createActor(this, id, actorKey, Math.round(x), Math.round(y), facing);
    this.actors.set(id, a);
    return a;
  }

  /** Dialogue state goes through the stage's `state` when the caller supplied one (else the game context). */
  protected createDialogueHooks(): DialogueHooks {
    const base = hooksFromContext(ctxOf(this));
    const st = this.stageData.state;
    if (!st) return base;
    return {
      ...base,
      hasFlag: (f) => (st.hasFlag ? st.hasFlag(f) : base.hasFlag(f)),
      hasMark: (m) => st.hasMark(m),
      setMark: (m) => st.setMark(m),
      setFlag: (f) => st.setFlag(f),
    };
  }

  protected createHost(): CinematicHost {
    return createSceneCinematicHost(this, {
      dialogue: this.dialogue as DialogueRunner,
      actors: this.actors,
      state: this.stageData.state,
      letter: this.stageData.letter,
      memory: () => { console.warn('[memory-stage] nested launchMemory ignored'); return Promise.resolve('completed' as const); },
    });
  }

  private wireInput(): void {
    const kb = this.input.keyboard;
    const advance = () => this.dialogue?.advance();
    const skip = () => { this.player?.skip(); };
    kb?.on('keydown-ESC', skip);
    kb?.on('keydown-X', skip);
    kb?.on('keydown-ENTER', advance);
    kb?.on('keydown-SPACE', advance);
    kb?.on('keydown-Z', advance);
    this.input.on('pointerdown', advance);
  }

  private async runTimeline(): Promise<void> {
    const player = this.player as CinematicPlayer;
    let result: CinematicResult = 'completed';
    try { result = await player.play(this.stageData.timelineId, { fastForward: this.stageData.fastForward }); }
    catch (e) { console.error('[memory-stage] timeline failed', e); }
    this.finish(result);
  }

  protected finish(result: CinematicResult): void {
    if (this.finished) return;
    this.finished = true;
    this.events.emit(MEMORY_DONE, result);
    this.stageData.onDone?.(result);
    if (this.scene.isActive() || this.scene.isPaused()) this.scene.stop();
  }

  private teardown(): void {
    this.input.keyboard?.removeAllListeners();
    this.input.removeAllListeners();
    // External stop while the timeline is in flight (router navigation, duplicate launch): unwind the
    // player now so its input:cancel subscription and ctx.skipHandler slot are not orphaned.
    this.player?.abort();
    this.dialogue?.abort();
    for (const a of this.actors.values()) { try { a.destroy(); } catch { /* ignore */ } }
    this.actors.clear();
    this.player = null;
    this.dialogue = null;
    if (!this.finished) { this.finished = true; this.stageData.onDone?.('completed'); }
  }
}

export interface RunMemoryStageOptions {
  fastForward?: boolean;
  state?: CinematicStateAccess;
  dialogueBox?: IDialogueBox;
  letter?: MemoryStageData['letter'];
  /** Pause the caller while the stage runs (default true). */
  pauseCaller?: boolean;
}

/**
 * Launches the memory stage scene above `from`, pauses `from`, and resolves with the stage result
 * once it has stopped. Registers the base MemoryStage (or the def's subclass) on first use.
 */
export function runMemoryStage(from: Phaser.Scene, stageKey: string, timelineId: string, opts: RunMemoryStageOptions = {}): Promise<CinematicResult> {
  const def = MEMORY_STAGES[stageKey];
  const sceneKey = def?.sceneKey ?? SceneKeys.MEMORY_STAGE;
  const mgr = from.scene;
  if (!mgr.get(sceneKey)) {
    const added = mgr.add(sceneKey, def?.sceneClass ?? MemoryStage, false);
    if (!added || !mgr.get(sceneKey)) { console.warn(`[memory-stage] could not register scene "${sceneKey}" — stage skipped`); return Promise.resolve('completed'); }
  }
  // A launch is applied by the scene manager at the next step, so isActive/isPaused cannot see a request
  // made earlier in the same frame (key bursts): LAUNCHING covers that window.
  if (LAUNCHING.has(sceneKey) || mgr.isActive(sceneKey) || mgr.isPaused(sceneKey)) { console.warn(`[memory-stage] "${sceneKey}" is already running — stage skipped`); return Promise.resolve('completed'); }
  const pauseCaller = opts.pauseCaller ?? true;
  return new Promise<CinematicResult>((resolve) => {
    let settled = false;
    const stage = mgr.get(sceneKey) as Phaser.Scene;
    const settle = (result: CinematicResult) => {
      if (settled) return;
      settled = true;
      LAUNCHING.delete(sceneKey);
      stage.events.off(Phaser.Scenes.Events.SHUTDOWN, onShutdown);
      if (pauseCaller && from.scene.isPaused()) from.scene.resume();
      resolve(result);
    };
    const onShutdown = () => settle('completed');
    const data: MemoryStageData = { stageKey, timelineId, callerKey: from.scene.key, fastForward: opts.fastForward, state: opts.state, dialogueBox: opts.dialogueBox, letter: opts.letter, onDone: settle };
    stage.events.once(Phaser.Scenes.Events.SHUTDOWN, onShutdown);
    LAUNCHING.add(sceneKey);
    mgr.launch(sceneKey, data);
    mgr.bringToTop(sceneKey);
    if (mgr.get(SceneKeys.UI)) mgr.bringToTop(SceneKeys.UI);
    if (pauseCaller) mgr.pause(from.scene.key);
  });
}
